import { ChargeEventRow } from "../models/chargeEventRow";
import { ConsumptionRow } from "../models/consumptionRow";
import { EnergyDevice } from "../models/energyDevice";
import { FuelUpEventRow } from "../models/fuelUpEventRow";
import { pendingConsumptionRow } from "../models/pendingConsumptionRow";
import { DeviceEnergy, failedDeviceEnergy } from "./fetchDeviceEnergy";

// Each arrival combines the whole fleet again, so the placeholder rows are built once per vehicle
// and reused; the rows of a vehicle that answered are already built once, in its own query.
const pendingRows = new WeakMap<EnergyDevice, ConsumptionRow>();
const failedRows = new WeakMap<EnergyDevice, ConsumptionRow>();
const notReadRows = new WeakMap<EnergyDevice, ConsumptionRow>();

function notReadConsumptionRow(device: EnergyDevice): ConsumptionRow {
  return { ...pendingConsumptionRow(device), status: "notRead" };
}

function cachedRow(
  cache: WeakMap<EnergyDevice, ConsumptionRow>,
  device: EnergyDevice,
  build: (device: EnergyDevice) => ConsumptionRow,
): ConsumptionRow {
  let row = cache.get(device);
  if (!row) {
    row = build(device);
    cache.set(device, row);
  }
  return row;
}

export interface EnergyReportProgress {
  /** Vehicles in the report. */
  total: number;
  /** Vehicles that have been read, whether successfully or not. */
  read: number;
  /** Vehicles whose request failed. */
  failed: number;
  isLoading: boolean;
}

export interface EnergyReport {
  consumptionRows: ConsumptionRow[];
  chargeEventRows: ChargeEventRow[];
  fuelUpEventRows: FuelUpEventRow[];
  progress: EnergyReportProgress;
  /** The first error, so the page can name what went wrong rather than only counting it. */
  error: Error | null;
}

/** The part of a query result this needs, so tests do not have to build a whole one. */
export interface DeviceEnergyResult {
  data?: DeviceEnergy;
  isPending: boolean;
  isError: boolean;
  error?: Error | null;
}

/**
 * Merges the per-vehicle results into the three views the report shows.
 *
 * Results arrive one vehicle at a time over several minutes, so this has to produce a complete
 * fleet at every point in between: a vehicle not yet read keeps its name and plate with every
 * figure missing, and one whose request failed says so on its row rather than disappearing from
 * the list. A fleet that silently shrinks while loading is the one outcome to avoid — it reads
 * exactly like a fleet with fewer vehicles in it.
 *
 * @param devices - The vehicles, in the order the results were requested.
 * @param results - The query results, one per vehicle, in the same order.
 * @param stopped - The user stopped the read: vehicles not read by then say so instead of loading.
 * @returns See {@link EnergyReport}.
 */
export function combineEnergyReport(
  devices: EnergyDevice[],
  results: readonly DeviceEnergyResult[],
  stopped = false,
): EnergyReport {
  const consumptionRows: ConsumptionRow[] = [];
  const chargeEventRows: ChargeEventRow[] = [];
  const fuelUpEventRows: FuelUpEventRow[] = [];
  let read = 0;
  let failed = 0;
  let error: Error | null = null;

  devices.forEach((device, index) => {
    const result = results[index];

    if (!result || result.isPending) {
      consumptionRows.push(
        stopped
          ? cachedRow(notReadRows, device, notReadConsumptionRow)
          : cachedRow(pendingRows, device, pendingConsumptionRow),
      );
      return;
    }

    read++;

    if (result.isError || !result.data) {
      failed++;
      error ??= result.error ?? null;
      consumptionRows.push(cachedRow(failedRows, device, d => failedDeviceEnergy(d).consumption));
      return;
    }

    consumptionRows.push(result.data.consumption);
    chargeEventRows.push(...result.data.chargeEvents);
    fuelUpEventRows.push(...result.data.fuelUps);
  });

  return {
    consumptionRows,
    chargeEventRows,
    fuelUpEventRows,
    progress: {
      total: devices.length,
      read,
      failed,
      isLoading: read < devices.length,
    },
    error,
  };
}
